/* eslint-disable import/no-anonymous-default-export */
// validator.js

/**
 * Convierte el input a un número.
 * Si el input es un string, elimina los puntos (separador de miles)
 * y reemplaza la coma (separador decimal) por un punto.
 * Si el resultado es -0, retorna 0.
 *
 * @param {string | number} input - El valor a convertir en número.
 * @returns {number} - El número convertido o 0 si no se puede convertir.
 */
// validator.js

/**
 * Obtiene la configuración regional del localStorage
 * @returns {Object} Configuración de separadores decimales y de miles con sus valores 'default'
 */
const getRegionalConfig = () => {
  try {
    const config = JSON.parse(localStorage.getItem('systemSettings'));
    // Devuelve la configuración completa o un objeto vacío si no existe
    return config?.REGIONAL || {};
  } catch (error) {
    console.error('Error leyendo configuración regional:', error);
    return {};
  }
};

/**
 * Convierte el input a un número según la configuración regional.
 * Elimina el separador de miles y normaliza el separador decimal a punto.
 * Si el resultado es -0, retorna 0.
 *
 * @param {string | number} input - El valor a convertir en número.
 * @returns {number} - El número convertido o 0 si no se puede convertir.
 */
export const parseToNumber = (input) => {
  if (typeof input === "string") {
    // Obtener la configuración actual DEL LOCALSTORAGE
    const config = getRegionalConfig();
    const decimalType = config.decimalSeparator?.default || "point"; // Valor por defecto si no hay config
    const thousandsType = config.thousandsSeparator?.default || "comma"; // Valor por defecto si no hay config
    
    // Mapear los tipos a caracteres reales (según tu JSON)
    const separatorMap = {
      comma: ',',
      point: '.',
      space: ' '
    };
    
    const decimalChar = separatorMap[decimalType]; // Ej: ',' para "comma"
    const thousandsChar = separatorMap[thousandsType]; // Ej: '.' para "point"
    
    let cleaned = input.replace(new RegExp(`[^0-9${decimalChar}]`, 'g'), '');
    
    // Primero: eliminar el separador de miles (solo si es diferente al decimal)
    if (thousandsChar && thousandsChar !== decimalChar) {
      // Escapamos el carácter para usarlo en regex
      const escapedThousands = thousandsChar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const thousandsRegex = new RegExp(escapedThousands, 'g');
      cleaned = cleaned.replace(thousandsRegex, '');
    }
    
    // Segundo: reemplazar el separador decimal por punto
    if (decimalChar && decimalChar !== '.') {
      const escapedDecimal = decimalChar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const decimalRegex = new RegExp(escapedDecimal, 'g');
      cleaned = cleaned.replace(decimalRegex, '.');
    }
    
    const num = Number(cleaned);
    return Object.is(num, -0) ? 0 : (isNaN(num) ? 0 : num);
  }
  
  return Object.is(input, -0) ? 0 : (typeof input === "number" ? input : 0);
};

/**
 * Formatea un número utilizando la configuración regional de Chile ("es-CL")
 * con tres dígitos decimales. Primero convierte el input a número usando parseToNumber.
 *
 * @param {string | number} input - El valor a formatear.
 * @returns {string} - El número formateado con 3 dígitos decimales.
 */
export const formatNumberWithComma = (input) => {
  if (input === null || input === undefined || input === '') {
    return '0';
  }

  // Convertir a número primero
  const numberValue = parseToNumber(input);
  
  // Obtener configuración regional
  const config = getRegionalConfig();
  const decimalPlaces = config.decimalPlaces?.default ?? 2; // Default a 2 si no está definido
  const alwaysShowDecimals = config.alwaysShowDecimals?.default ?? false; // Default a false si no está definido
  const decimalType = config.decimalSeparator?.default || "point";
  const thousandsType = config.thousandsSeparator?.default || "comma";
  
  // Determinar la localidad basada en la configuración
  let locale;
  if (decimalType === "comma" && thousandsType === "point") {
    locale = 'es-CL'; // Formato chileno: 1.234,56
  } else if (decimalType === "point" && thousandsType === "comma") {
    locale = 'en-US'; // Formato americano: 1,234.56
  } else if (decimalType === "comma" && thousandsType === "space") {
    locale = 'fr-FR'; // Formato francés: 1 234,56
  } else {
    locale = 'es-CL'; // Por defecto
  }
  
  // Formatear el número según la localidad detectada
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: alwaysShowDecimals ? decimalPlaces : 0,
    maximumFractionDigits: decimalPlaces
  }).format(numberValue);
};

/**
 * Formateo personalizado para combinaciones no estándar
 */
const customFormatNumber = (number, decimalType, thousandsType) => {
  const separatorMap = {
    comma: ',',
    point: '.',
    space: ' '
  };
  
  const decimalSep = separatorMap[decimalType] || '.';
  const thousandsSep = separatorMap[thousandsType] || ',';
  
  // Formatear con Intl a un formato base y luego reemplazar
  const baseFormatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  }).format(number);
  
  let [integerPart, decimalPart] = baseFormatted.split('.');
  
  // Aplicar separador de miles personalizado
  if (thousandsSep !== ',') {
    integerPart = integerPart.replace(/,/g, thousandsSep);
  }
  
  // Aplicar separador decimal personalizado
  if (decimalPart && decimalSep !== '.') {
    return `${integerPart}${decimalSep}${decimalPart}`;
  }
  
  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
};

/**
 * Formatea un número como moneda según el tipo de moneda especificado.
 * Utiliza formatos estándar para CLP, USD, EURO y un formato especial para UF.
 *
 * @param {string | number} input - El valor a formatear.
 * @param {string} currencyCode - El código de la moneda (CLP, UF, USD, EURO).
 * @returns {string} - El número formateado como moneda. Retorna 'N/A' si el input no es válido.
 */
export const formatCurrency = (input, currencyCode) => {
  if (!currencyCode) {
    console.warn("Currency code is undefined. Falling back to default 'CLP'.");
    currencyCode = 'CLP'; // Default to CLP if not provided
  }

  const numberValue = parseToNumber(input);

  if (isNaN(numberValue)) {
    return 'N/A';
  }

  // Handle undefined or null currencyCode
  if (!currencyCode) {
    console.warn("Currency code is undefined. Falling back to generic number format with 2 decimal places.");
    // Generic number format with 2 decimal places, no currency symbol
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numberValue);
  }

  let locale = '';
  let options = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0, // Default, will be overridden
    maximumFractionDigits: 0, // Default, will be overridden
  };

  let formattedString;

  switch (currencyCode.toUpperCase()) {
    case 'CLP':
      locale = 'es-CL';
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
      formattedString = new Intl.NumberFormat(locale, options).format(numberValue);
      break;
    case 'UF':
      locale = 'es-CL';
      const ufNumberOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
      };
      const ufFormatter = new Intl.NumberFormat(locale, ufNumberOptions);
      formattedString = `UF ${ufFormatter.format(numberValue)}`;
      break;
    case 'USD':
      locale = 'es-CL'; // Cambiado a es-CL para usar coma como decimal y punto como miles
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
      formattedString = new Intl.NumberFormat(locale, options).format(numberValue);
      break;
    case 'EURO':
      locale = 'es-CL'; // Cambiado a es-CL para usar coma como decimal y punto como miles
      options.currency = 'EUR';
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
      formattedString = new Intl.NumberFormat(locale, options).format(numberValue);
      break;
    default:
      console.warn(`Unknown currency code: ${currencyCode}. Falling back to generic number format with 2 decimal places.`);
      // Fallback to generic number format with 2 decimal places, no currency symbol
      formattedString = new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numberValue);
  }

  try {
    return formattedString;
  } catch (error) {
    console.error(`Error formatting currency for ${currencyCode}:`, error);
    // Fallback in case of error during specific formatting
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numberValue);
  }
};

/**
 * Formatea una fecha en formato "YYYY-MM-DD" a "DD/MM/YYYY".
 * Si el input no tiene el formato correcto, retorna 'N/A'.
 *
 * @param {string} dateStr - Fecha en formato "YYYY-MM-DD".
 * @returns {string} - Fecha formateada como "DD/MM/YYYY" o 'N/A' si es inválida.
 */
export const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return 'N/A';

  // Extrae solo la parte de la fecha si tiene hora
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) return 'N/A';

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * Formatea una fecha en formato "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss" a "DD/MM/YYYY HH:mm".
 * Si el input no tiene el formato correcto, retorna 'N/A'.
 *
 * @param {string} dateStr - Fecha en formato "YYYY-MM-DD" o con hora incluida.
 * @returns {string} - Fecha formateada como "DD/MM/YYYY HH:mm" o 'N/A' si es inválida.
 */
export const formatedDateTime = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return 'N/A';

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses base 0
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Devuelve un objeto de estilo para alinear valores numéricos a la derecha
 * @returns {Object} Objeto de estilo con alineación a la derecha
 */
export const rightAlignedNumberStyle = () => {
  return {
    textAlign: 'right',
    display: 'block',
    width: '100%'
  };
};

/**
 * Devuelve un objeto de estilo estandarizado para los estados en toda la aplicación
 * @param {string} status - El estado para el cual se requiere el estilo
 * @returns {Object} Objeto con los estilos CSS para el estado
 */
export const getStatusStyle = (status) => {
  // Paleta de colores estandarizada
  const statusStyles = {
    // Colores base para todos los estados
    // Verde - para estados positivos/completados
    SUCCESS_COLOR: '#28a745',
    SUCCESS_BG: '#eafaf1',
    // Amarillo - para estados de advertencia/pendientes
    WARNING_COLOR: '#ff9900',
    WARNING_BG: '#fffbe7',
    // Rojo - para estados negativos/errores/cancelados
    ERROR_COLOR: '#dc3545',
    ERROR_BG: '#ffe6e6',
    // Azul - para estados informativos/en proceso
    INFO_COLOR: '#0066cc',
    INFO_BG: '#e6f3ff',
    // Gris - para estados neutrales/inactivos
    NEUTRAL_COLOR: '#6c757d',
    NEUTRAL_BG: '#e2e3e5',
    
    // Estados generales estandarizados
    SUCCESS: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    WARNING: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    ERROR: { color: '#dc3545', backgroundColor: '#ffe6e6', border: '1px solid #dc3545' },
    INFO: { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    NEUTRAL: { color: '#6c757d', backgroundColor: '#e2e3e5', border: '1px solid #6c757d' },
    
    // Estados específicos de rampas - estandarizados
    AVAILABLE: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    MAINTENANCE: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    OCCUPIED: { color: '#6c757d', backgroundColor: '#e2e3e5', border: '1px solid #6c757d' },
    FINALIZED: { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    
    // Estados específicos de GRN - estandarizados
    COMPLETED: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    PENDING: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    CANCELLED: { color: '#dc3545', backgroundColor: '#ffe6e6', border: '1px solid #dc3545' },
    IN_PROGRESS: { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    
    // Estados específicos de ASN - estandarizados
    Draft: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' }, // Cambiado a naranja
    Approved: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    Received: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    Pending: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    Modified: { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    Shipped: { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    /** OV: despacho parcial (`PARTIALLY_SHIPPED`). Naranjo más marcado que `Pending` (#ff9900) para no confundir con «creada». */
    PartiallyShipped: { color: '#e65100', backgroundColor: '#ffe0b2', border: '1px solid #e65100' },
    /** OV: cerrada sin completar despacho (`CLOSED`). */
    Closed: { color: '#5c6bc0', backgroundColor: '#e8eaf6', border: '1px solid #5c6bc0' },
    Canceled: { color: '#dc3545', backgroundColor: '#ffe6e6', border: '1px solid #dc3545' },
    'In transit': { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    Delivered: { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    Returned: { color: '#dc3545', backgroundColor: '#ffe6e6', border: '1px solid #dc3545' },
    Shipment: { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    
    // Estados de órdenes de compra - estandarizados
    'Open': { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    'Approved': { color: '#28a745', backgroundColor: '#eafaf1', border: '1px solid #28a745' },
    'Shipped': { color: '#0066cc', backgroundColor: '#e6f3ff', border: '1px solid #0066cc' },
    'Pending': { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    'Canceled': { color: '#dc3545', backgroundColor: '#ffe6e6', border: '1px solid #dc3545' },
    
    // Agendamientos: mismo estilo rojo que `CANCELLED` (GRN, arriba).
    'DELAYED': { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    'PENDING': { color: '#ff9900', backgroundColor: '#fffbe7', border: '1px solid #ff9900' },
    
    // Valor por defecto
    Default: { color: '#28a745', backgroundColor: '#28a745', border: '1px solid #6c757d' }
  };
  
  // Devuelve el estilo correspondiente al estado o el estilo por defecto
  return statusStyles[status] || statusStyles.Default;
};

/**
 * Devuelve un objeto con las propiedades para crear un componente ObjectStatus estandarizado
 * @param {string} status - El estado para el cual se requiere el componente
 * @param {string} [label=null] - El texto a mostrar en el componente (opcional)
 * @param {Object} [additionalStyles={}] - Estilos adicionales a aplicar (opcional)
 * @returns {Object} Objeto con las propiedades para el componente ObjectStatus
 */
export const getStatusComponentProps = (status, label = null, additionalStyles = {}) => {
  const baseStyle = getStatusStyle(status);
  
  return {
    style: {
      marginLeft: '15px',
      width: 'auto',
      minWidth: '100px',
      borderRadius: '4px',
      padding: '5px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      textAlign: 'center',
      display: 'inline-block',
      ...baseStyle,
      ...additionalStyles
    }
  };
};

/**
 * Devuelve un componente Text con el número formateado y alineado a la derecha
 * @param {number|string} value - El valor numérico a formatear
 * @param {Object} additionalStyles - Estilos adicionales a aplicar (opcional)
 * @returns {JSX.Element} Componente Text con el número formateado
 */
export const formattedNumberCell = (value, currencyCode = null, additionalStyles = {}) => {
  const formattedValue = currencyCode ? formatCurrency(value, currencyCode) : formatNumberWithComma(value);
  
  return {
    content: formattedValue,
    style: {
      textAlign: 'right',
      display: 'block',
      width: '100%',
      ...additionalStyles
    }
  };
};

export default {
  parseToNumber,
  formatNumberWithComma,
  formatDateToDDMMYYYY,
  formatedDateTime,
  rightAlignedNumberStyle,
  formattedNumberCell,
  getStatusStyle,
  getStatusComponentProps,
  formatCurrency
};
